import { ref, computed, toValue, type MaybeRefOrGetter } from 'vue';
import type { Product } from '@shared/types/domain';
import { modifierPriceAdjustment } from '@shared/lib/modifier-price';

/**
 * Product detail selection + validation.
 *
 * IMPORTANT: product must be a getter/ref — MenuPage mounts the modal with
 * selectedProduct=null and only later assigns the product. A plain value
 * captured at setup freezes product=null forever and makes isSelectionValid
 * always true (QA-20260805-CLI-MOD-002).
 */
export function useProductDetail(productSource: MaybeRefOrGetter<Product | null>) {
  const quantity = ref(1);
  const selectedModifiers = ref<Record<string, Array<string | number>>>({});
  const selectedComponentModifiers = ref<Record<string, Array<string | number>>>({});
  const NO_SELECTION_PREFIX = 'none:';

  const product = computed(() => toValue(productSource) ?? null);

  const getComponentGroupKey = (
    component: any,
    componentIndex: number,
    groupId: string | number,
  ): string => `component:${component.component_item_id || component.menu_item_id || componentIndex}:${String(groupId)}`;

  const resetState = (newProduct: Product | null, initialItem?: any) => {
    if (newProduct) {
      quantity.value = Number(initialItem?.quantity || 1);
      selectedModifiers.value = {};
      selectedComponentModifiers.value = {};

      const itemMods = Array.isArray(initialItem?.modifiers) ? initialItem.modifiers : [];
      const itemNoneGroups = Array.isArray(initialItem?.modifier_none_groups)
        ? initialItem.modifier_none_groups.map(String)
        : [];

      newProduct.modifier_groups?.forEach((group) => {
        const key = String(group.id);
        const groupModIds = (group.modifiers || []).map((m) => m.id);
        const selectedForGroup = itemMods.filter((mId: string | number) => groupModIds.includes(mId));

        if (itemNoneGroups.includes(key)) {
          selectedModifiers.value[key] = [`${NO_SELECTION_PREFIX}${key}`];
        } else {
          selectedModifiers.value[key] = selectedForGroup;
        }
      });

      (newProduct.package_components || []).forEach((component, componentIndex) => {
        const initialComp = (initialItem?.package_components || [])[componentIndex];
        const compMods = Array.isArray(initialComp?.modifiers) ? initialComp.modifiers : [];
        const compNoneGroups = Array.isArray(initialComp?.modifier_none_groups)
          ? initialComp.modifier_none_groups.map(String)
          : [];

        (component.modifier_groups || []).forEach((group) => {
          const groupKey = getComponentGroupKey(component, componentIndex, group.id);
          const groupModIds = (group.modifiers || []).map((m) => m.id);
          const selectedForCompGroup = compMods.filter((mId: string | number) => groupModIds.includes(mId));

          if (compNoneGroups.includes(String(group.id))) {
            selectedComponentModifiers.value[groupKey] = [`${NO_SELECTION_PREFIX}${groupKey}`];
          } else {
            selectedComponentModifiers.value[groupKey] = selectedForCompGroup;
          }
        });
      });
    }
  };

  const calculateExtrasTotal = () => {
    const current = product.value;
    if (!current) return 0;
    let total = 0;
    (current.modifier_groups || []).forEach((group) => {
      const selection = selectedModifiers.value[String(group.id)];
      if (selection) {
        selection.forEach((modId) => {
          const modifier = group.modifiers.find((m) => m.id === modId);
          if (modifier) total += modifierPriceAdjustment(modifier as any);
        });
      }
    });
    (current.package_components || []).forEach((component, componentIndex) => {
      (component.modifier_groups || []).forEach((group) => {
        const key = getComponentGroupKey(component, componentIndex, group.id);
        const selection = selectedComponentModifiers.value[key] || [];
        selection.forEach((modId) => {
          const modifier = group.modifiers.find((m) => m.id === modId);
          if (modifier) total += modifierPriceAdjustment(modifier as any);
        });
      });
    });
    return total;
  };

  // Catalog may emit min_required (menu_catalog_service) or min_selection (menu_query_service).
  const getGroupMinSelection = (group: any): number => {
    const raw = group?.min_selection ?? group?.min_required ?? group?.min_selections;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return group?.is_required ? 1 : 0;
  };

  const groupAllowsNoSelection = (group: any): boolean => {
    const min = getGroupMinSelection(group);
    if (group.is_required || min > 0) return false;
    return group.allow_none_option !== false;
  };
  const getNoSelectionId = (groupId: string | number): string => `${NO_SELECTION_PREFIX}${String(groupId)}`;

  const getSelectionCount = (groupId: string | number): number => {
    const selected = selectedModifiers.value[String(groupId)] || [];
    const hasNoSelection = selected.some((id) => String(id).startsWith(NO_SELECTION_PREFIX));
    return hasNoSelection ? 1 : selected.length;
  };

  const getGroupMaxSelection = (group: any): number => {
    const configuredMax = Number(group.max_selection ?? group.max_allowed ?? 0);
    if (configuredMax > 0) return configuredMax;
    const minSelection = getGroupMinSelection(group);
    const selectableModifiers = (group.modifiers || []).filter(
      (modifier: Record<string, unknown>) =>
        !String(modifier.id).startsWith(NO_SELECTION_PREFIX) && modifier.is_available !== false,
    ).length;
    return Math.max(minSelection, selectableModifiers, 1);
  };

  const getComponentSelectionCount = (componentKey: string): number => {
    const selected = selectedComponentModifiers.value[componentKey] || [];
    const hasNoSelection = selected.some((id) => String(id).startsWith(NO_SELECTION_PREFIX));
    return hasNoSelection ? 1 : selected.length;
  };

  const getComponentGroupMaxSelection = (group: any): number => {
    const configuredMax = Number(group.max_selection ?? group.max_allowed ?? 0);
    if (configuredMax > 0) return configuredMax;
    const minSelection = getComponentGroupMinSelection(group);
    const selectableModifiers = (group.modifiers || []).filter(
      (modifier: Record<string, unknown>) =>
        !String(modifier.id).startsWith(NO_SELECTION_PREFIX) && modifier.is_available !== false,
    ).length;
    return Math.max(minSelection, selectableModifiers, 1);
  };

  const getComponentGroupMinSelection = (group: any): number => {
    const raw = group?.min_selection ?? group?.min_required ?? group?.min_selections;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return group?.is_required ? 1 : 0;
  };

  const invalidGroups = computed(() => {
    const current = product.value;
    if (!current) return [];
    const invalidNames: string[] = [];
    (current.modifier_groups || []).forEach((group) => {
      const min = getGroupMinSelection(group);
      if (min > 0) {
        const selected = selectedModifiers.value[String(group.id)] || [];
        const hasNoSelection = selected.some((id) => String(id).startsWith(NO_SELECTION_PREFIX));
        const count = hasNoSelection ? 1 : selected.length;
        if (count < min) {
          invalidNames.push(group.name);
        }
      }
    });
    (current.package_components || []).forEach((component, componentIndex) => {
      (component.modifier_groups || []).forEach((group) => {
        const min = getComponentGroupMinSelection(group);
        if (min > 0) {
          const groupKey = getComponentGroupKey(component, componentIndex, group.id);
          const selected = selectedComponentModifiers.value[groupKey] || [];
          const hasNoSelection = selected.some((id) => String(id).startsWith(NO_SELECTION_PREFIX));
          const count = hasNoSelection ? 1 : selected.length;
          if (count < min) {
            invalidNames.push(`${component.name} - ${group.name}`);
          }
        }
      });
    });
    return invalidNames;
  });

  const isSelectionValid = computed(() => {
    // No product selected → cannot add to cart (avoids stale null product always-true)
    if (!product.value) return false;
    return invalidGroups.value.length === 0;
  });

  const toggleModifier = (groupId: string | number, modifierId: string | number, max: number) => {
    const currentProduct = product.value;
    const key = String(groupId);
    const modifierKey = String(modifierId);
    const noSelectionId = getNoSelectionId(key);
    const current = [...(selectedModifiers.value[key] || [])];
    const index = current.indexOf(modifierId);
    const hasNoSelection = current.includes(noSelectionId);

    if (modifierKey.startsWith(NO_SELECTION_PREFIX)) {
      const group = currentProduct?.modifier_groups?.find((candidate) => String(candidate.id) === key);
      if (group && !groupAllowsNoSelection(group)) return;
      selectedModifiers.value[key] = index > -1 ? [] : [noSelectionId];
      return;
    }
    if (hasNoSelection) {
      const noSelectionIndex = current.indexOf(noSelectionId);
      if (noSelectionIndex > -1) current.splice(noSelectionIndex, 1);
    }
    if (index > -1) current.splice(index, 1);
    else if (max === 1) current.splice(0, current.length, modifierId);
    else if (max === 0 || current.length < max) current.push(modifierId);
    selectedModifiers.value[key] = current;
  };

  const toggleComponentModifier = (
    component: any,
    componentIndex: number,
    group: any,
    modifierId: string | number,
    max: number,
  ) => {
    const key = getComponentGroupKey(component, componentIndex, group.id);
    const modifierKey = String(modifierId);
    const noSelectionId = `${NO_SELECTION_PREFIX}${key}`;
    const current = [...(selectedComponentModifiers.value[key] || [])];
    const index = current.indexOf(modifierId);
    const hasNoSelection = current.includes(noSelectionId);

    if (modifierKey.startsWith(NO_SELECTION_PREFIX)) {
      if (!groupAllowsNoSelection(group)) return;
      selectedComponentModifiers.value[key] = index > -1 ? [] : [noSelectionId];
      return;
    }
    if (hasNoSelection) {
      const noSelectionIndex = current.indexOf(noSelectionId);
      if (noSelectionIndex > -1) current.splice(noSelectionIndex, 1);
    }
    if (index > -1) current.splice(index, 1);
    else if (max === 1) current.splice(0, current.length, modifierId);
    else if (max === 0 || current.length < max) current.push(modifierId);
    selectedComponentModifiers.value[key] = current;
  };

  return {
    quantity,
    selectedModifiers,
    selectedComponentModifiers,
    NO_SELECTION_PREFIX,
    getComponentGroupKey,
    resetState,
    calculateExtrasTotal,
    groupAllowsNoSelection,
    getNoSelectionId,
    getSelectionCount,
    getGroupMaxSelection,
    getGroupMinSelection,
    getComponentSelectionCount,
    getComponentGroupMaxSelection,
    getComponentGroupMinSelection,
    invalidGroups,
    isSelectionValid,
    toggleModifier,
    toggleComponentModifier,
  };
}
